import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronRight, LuSearch, LuUserX } from 'react-icons/lu';
import { Pagination, PersonChip } from 'components/common';
import { TH_CLS, TD_CLS } from 'helpers/formStyles';
import { formatInZone, slotLabel, zoneOffsetLabel } from './reminderFormat';
import ReminderItemList from './ReminderItemList';

// Below a reminder's settings: who would get what if it went out now, items no
// active person receives, and the last runs. Columns come from the reminder itself
// (backend preview_columns), so this works for every module.

const TONE = {
  bad: 'tw-bg-red-50 tw-text-red-700',
  warn: 'tw-bg-amber-50 tw-text-amber-700',
  muted: 'tw-bg-slate-100 tw-text-slate-600',
  strong: 'tw-bg-transparent tw-text-slate-900',
};
const STATUS_TONE = {
  sent: 'tw-bg-emerald-50 tw-text-emerald-700', failed: 'tw-bg-red-50 tw-text-red-700',
  missed: 'tw-bg-red-50 tw-text-red-700', running: 'tw-bg-amber-50 tw-text-amber-700',
};

const Pill = ({ tone, children, title }) => (
  <span title={title} className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[11px] tw-font-semibold tw-tabular-nums ${tone}`}>
    {children}
  </span>
);
Pill.propTypes = { tone: PropTypes.string, children: PropTypes.node, title: PropTypes.string };

const Section = ({ title, subtitle, children }) => (
  <section aria-label={title} className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-overflow-clip">
    <header className="tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
      <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-slate-900">{title}</h3>
      {subtitle && <p className="tw-m-0 tw-text-[12px] tw-text-slate-500">{subtitle}</p>}
    </header>
    {children}
  </section>
);
Section.propTypes = { title: PropTypes.string.isRequired, subtitle: PropTypes.string, children: PropTypes.node };

const Empty = ({ children }) => <div className="tw-py-8 tw-text-center tw-text-sm tw-text-slate-500">{children}</div>;
Empty.propTypes = { children: PropTypes.node };

// A recipient's row opens to list the items their email would carry.
const RecipientRow = ({ row, columns, timeZone }) => {
  const [open, setOpen] = useState(false);
  const panelId = `reminder-items-${row.key || row.email}`;
  const hasItems = (row.items || []).length > 0;
  return (
    <>
      <tr className={open ? 'tw-bg-slate-50' : undefined}>
        <td className={TD_CLS}>
          <button type="button" onClick={() => setOpen((v) => !v)} disabled={!hasItems}
            aria-expanded={open} aria-controls={panelId}
            className="tw-flex tw-items-start tw-gap-2 tw-w-full tw-text-left tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer disabled:tw-cursor-default">
            <LuChevronRight size={14} aria-hidden="true"
              className={`tw-mt-1 tw-shrink-0 tw-text-slate-400 tw-transition-transform ${open ? 'tw-rotate-90' : ''} ${hasItems ? '' : 'tw-invisible'}`} />
            <span className="tw-min-w-0">
              <PersonChip name={row.name} fullName={row.name} email={row.email} size="sm" />
              {row.summary && <span className="tw-block tw-mt-0.5 tw-text-[12px] tw-text-slate-500">{row.summary}</span>}
            </span>
          </button>
        </td>
        {columns.map((c) => {
          const value = row.counts?.[c.key] || 0;
          return (
            <td key={c.key} className={`${TD_CLS} tw-text-center`}>
              {value > 0 ? <Pill tone={TONE[c.tone] || TONE.muted}>{value}</Pill> : <span className="tw-text-[12px] tw-text-slate-300">0</span>}
            </td>
          );
        })}
      </tr>
      {open && (
        <tr id={panelId}>
          <td colSpan={columns.length + 1} className={`${TD_CLS} tw-bg-slate-50 tw-pl-10`}>
            <ReminderItemList items={row.items} moreItems={row.more_items} timeZone={timeZone} />
          </td>
        </tr>
      )}
    </>
  );
};
RecipientRow.propTypes = { row: PropTypes.object.isRequired, columns: PropTypes.array.isRequired, timeZone: PropTypes.string.isRequired };

// Search shows once the list no longer fits on a screen; pages of 20 by default.
const SEARCH_FROM = 10;
const PAGE_SIZES = [20, 50, 100];

// What a search matches: the person, and what their email lists (code, payee, project).
const rowText = (row) => [row.name, row.email, row.summary, ...(row.items || []).flatMap((i) => [i.label, i.title])]
  .filter(Boolean).join(' ').toLowerCase();

const RecipientTable = ({ rows, columns, timeZone, total }) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZES[0]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((row) => rowText(row).includes(q)) : rows;
  }, [rows, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / perPage));
  const current = Math.min(page, pages);
  const shown = filtered.slice((current - 1) * perPage, current * perPage);

  return (
    <>
      {rows.length >= SEARCH_FROM && (
        <div className="tw-px-4 tw-py-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
          <label className="tw-relative tw-block tw-max-w-sm">
            <span className="tw-sr-only">Search recipients</span>
            <LuSearch size={13} className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" aria-hidden="true" />
            <input type="search" value={query} placeholder="Search name, email, code or project"
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-[13px] tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white" />
          </label>
        </div>
      )}
      {shown.length === 0 ? <Empty>No recipient matches.</Empty> : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse">
            <thead>
              <tr>
                <th className={TH_CLS}>Recipient</th>
                {columns.map((c) => <th key={c.key} className={`${TH_CLS} tw-text-center`}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => <RecipientRow key={row.key || row.email} row={row} columns={columns} timeZone={timeZone} />)}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > PAGE_SIZES[0] && (
        <div className="tw-px-4 tw-py-2 tw-border-0 tw-border-t tw-border-solid tw-border-slate-200">
          <Pagination currentPage={current} totalPages={pages} totalResults={filtered.length} perPage={perPage}
            pageSizeOptions={PAGE_SIZES} onPageChange={setPage}
            onPageSizeChange={(size) => { setPerPage(size); setPage(1); }} />
        </div>
      )}
      {total > rows.length && (
        <p className="tw-m-0 tw-px-4 tw-py-2 tw-text-[12px] tw-text-slate-500">
          Showing the first {rows.length} of {total} recipients — everyone still gets their email.
        </p>
      )}
    </>
  );
};
RecipientTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.array.isRequired,
  timeZone: PropTypes.string.isRequired,
  total: PropTypes.number,
};

export const RecipientPreview = ({ columns, preview, previewTotal, unassigned, summaryRecipients, timeZone }) => (
  <Section title="If it went out now"
    subtitle={`${previewTotal ?? preview.length} ${(previewTotal ?? preview.length) === 1 ? 'person' : 'people'} — open a row to see what their email would list. Refreshes when you save.`}>
    {preview.length === 0
      ? <Empty>Nothing is waiting long enough to remind anyone.</Empty>
      : <RecipientTable rows={preview} columns={columns} timeZone={timeZone} total={previewTotal} />}
    {summaryRecipients.length > 0 && (
      <p className="tw-m-0 tw-px-4 tw-py-2.5 tw-text-[12px] tw-text-slate-600 tw-bg-slate-50 tw-border-0 tw-border-t tw-border-solid tw-border-slate-200">
        Whole list also to {summaryRecipients.map((r) => r.name || r.email).join(', ')}.
      </p>
    )}
    {unassigned.length > 0 && (
      <div className="tw-px-4 tw-py-3 tw-bg-amber-50 tw-border-0 tw-border-t tw-border-solid tw-border-amber-200">
        <p className="tw-m-0 tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-semibold tw-text-amber-800">
          <LuUserX size={14} />
          {unassigned.length} {unassigned.length > 1 ? 'items have' : 'item has'} no active recipient — only the summary shows {unassigned.length > 1 ? 'them' : 'it'}
        </p>
        <ReminderItemList items={unassigned.map((u) => ({ ...u, key: u.key || u.label, step: u.step || u.detail }))} timeZone={timeZone} />
      </div>
    )}
  </Section>
);
RecipientPreview.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, tone: PropTypes.string })).isRequired,
  preview: PropTypes.arrayOf(PropTypes.object).isRequired,
  previewTotal: PropTypes.number,
  unassigned: PropTypes.arrayOf(PropTypes.object).isRequired,
  summaryRecipients: PropTypes.arrayOf(PropTypes.object).isRequired,
  timeZone: PropTypes.string.isRequired,
};

export const RunHistory = ({ definition, runs, timeZone }) => {
  // A scheduled task has no emails: its run says what it did instead.
  const task = definition.kind === 'task';
  return (
  <Section title="Recent runs" subtitle={task
    ? 'Scheduled runs are locked per day and slot, so the task runs once a day even if the scheduler retries.'
    : 'Scheduled runs are locked per day and slot, so nobody gets the same email twice.'}>
    {runs.length === 0 ? <Empty>{task ? 'It has not run yet.' : 'Nothing has been sent yet.'}</Empty> : (
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead>
            <tr>
              <th className={TH_CLS}>When ({zoneOffsetLabel(timeZone) || timeZone})</th>
              <th className={TH_CLS}>Run</th>
              <th className={TH_CLS}>Status</th>
              {task ? <th className={TH_CLS}>Result</th> : (
                <>
                  <th className={`${TH_CLS} tw-text-center`}>Emails</th>
                  <th className={`${TH_CLS} tw-text-center`}>Items</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id}>
                <td className={`${TD_CLS} tw-whitespace-nowrap`}>{formatInZone(run.started_at, timeZone)}</td>
                <td className={TD_CLS}>
                  {slotLabel(definition, run.slot)}
                  {run.trigger === 'manual' && <span className="tw-text-slate-500"> · {task ? 'run' : 'sent'} by {run.triggered_by || 'admin'}</span>}
                </td>
                <td className={TD_CLS}><Pill tone={STATUS_TONE[run.status] || TONE.muted} title={run.error || undefined}>{run.status}</Pill></td>
                {task ? (
                  <td className={`${TD_CLS} tw-text-[12px] ${run.error ? 'tw-text-red-700' : 'tw-text-slate-600'}`}>
                    {run.summary || run.error || '—'}
                  </td>
                ) : (
                  <>
                    <td className={`${TD_CLS} tw-text-center tw-tabular-nums`}>{run.recipients_count}</td>
                    <td className={`${TD_CLS} tw-text-center tw-tabular-nums`}>{run.items_count}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
  );
};
RunHistory.propTypes = {
  definition: PropTypes.object.isRequired,
  runs: PropTypes.arrayOf(PropTypes.object).isRequired,
  timeZone: PropTypes.string.isRequired,
};
